import { Box, Button, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { flexCenter } from '../commonUI/cssClasses';
import {
  OnboardingTabsEnum,
  selectCurrOnboardingTab,
  setCurrOnboardingTab,
} from './onboardingSlice';
const tabs = Object.values(OnboardingTabsEnum);

/**
 * Returns the appropriate color based on the provided active view.
 *
 * @param currentView - The currently active OnboardingTabsEnum value.
 * @param buttonView - The expected OnboardingTabsEnum value of the button.
 * @param theme - The current theme object which includes color definitions.
 * @return - The color corresponding to the provided active view. Returns bloomfieldGreen if the active view is the same as the expected button view, otherwise returns white.
 */
export function getActivePageViewColor(
  currentView: OnboardingTabsEnum,
  buttonView: OnboardingTabsEnum,
  theme: { colors: { common: { white: string; bloomfieldGreen: string } } }
): string {
  if (currentView === buttonView) {
    return theme.colors.common.bloomfieldGreen;
  } else {
    return theme.colors.common.white;
  }
}

const OnboardingHeader: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currTab = useAppSelector(selectCurrOnboardingTab);

  const spacer = (
    <Box
      sx={{
        display: 'flex',
        m: '0 1rem',
        color: theme.colors.darkTheme.grey.muted,
      }}
    >
      |
    </Box>
  );

  const tabButtons = useMemo(
    () =>
      tabs.map((tab) => (
        <Box key={tab} sx={{ ...flexCenter, flexDirection: 'row' }}>
          <Button
            sx={{
              p: '0',
              fontSize: '1.2rem !important',
              fontFamily: theme.fonts.primary,
              transition: `color 0.3s ease-in-out`,
              color: getActivePageViewColor(currTab, tab, theme),
            }}
            onClick={() => {
              dispatch(setCurrOnboardingTab(tab));
            }}
          >
            {t(tab, { ns: 'common' })}
          </Button>
          {/* don't show spacer for last tab */}
          {tab !== OnboardingTabsEnum.users && spacer}
        </Box>
      )),
    [currTab]
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '4rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: theme.colors.darkTheme.grey.medium,
        borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
        pb: 3,
        p: '0.5rem 1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          fontSize: '1rem',
          fontWeight: 500,
          '& button': {
            fontSize: '1rem',
            fontWeight: 700,
          },
        }}
      >
        {tabButtons}
      </Box>
    </Box>
  );
};

export default OnboardingHeader;
